import { Link } from "wouter";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { useLanguage } from "@/components/language-provider";
import { ArrowLeft, Building2, Send, CheckCircle2, Users, Globe, Shield, Headphones, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { WEBSITE_ROUTES, getApiUrl } from "@/lib/config";
import { AnimatedSection } from "@/components/landing/animations";
import { useToast } from "@/hooks/use-toast";
import { useContact } from "@/lib/website-settings";

export default function EnterpriseContact() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", employees: "", message: "", interest: "dynamics365",
  });
  // Hidden from people; bots that fill every field get a fake success.
  const [honeypot, setHoneypot] = useState("");
  const contact = useContact();

  const t = {
    title: language === "pt" ? "Fale com nossa equipe" : language === "es" ? "Hable con nuestro equipo" : "Talk to our team",
    subtitle: language === "pt"
      ? "Conte-nos sobre suas necessidades e nossa equipe entrará em contato em até 24h."
      : language === "es" ? "Cuéntenos lo que necesita y nuestro equipo se comunicará con usted en hasta 24 h." : "Tell us about your needs and our team will contact you within 24h.",
    name: language === "pt" ? "Nome completo" : language === "es" ? "Nombre completo" : "Full name",
    email: "Email",
    company: language === "pt" ? "Empresa" : language === "es" ? "Empresa" : "Company",
    phone: language === "pt" ? "Telefone" : language === "es" ? "Teléfono" : "Phone",
    employees: language === "pt" ? "Número de funcionários" : language === "es" ? "Cantidad de empleados" : "Number of employees",
    message: language === "pt" ? "Mensagem" : language === "es" ? "Mensaje" : "Message",
    interest: language === "pt" ? "Interesse principal" : language === "es" ? "Interés principal" : "Main interest",
    submit: language === "pt" ? "Enviar mensagem" : language === "es" ? "Enviar mensaje" : "Send message",
    sending: language === "pt" ? "Enviando..." : language === "es" ? "Enviando..." : "Sending...",
    successTitle: language === "pt" ? "Mensagem enviada!" : language === "es" ? "¡Mensaje enviado!" : "Message sent!",
    successMsg: language === "pt" ? "Nossa equipe entrará em contato em breve." : language === "es" ? "Nuestro equipo se comunicará con usted pronto." : "Our team will contact you soon.",
  };

  const interests = [
    { value: "dynamics365", label: "Dynamics 365" },
    { value: "api", label: "API Integration" },
    { value: "govbr", label: language === "pt" ? "Assinatura GOV.BR" : language === "es" ? "Firma GOV.BR" : "GOV.BR Signature" },
    { value: "enterprise", label: language === "pt" ? "Plano Enterprise" : language === "es" ? "Plan Enterprise" : "Enterprise Plan" },
    { value: "other", label: language === "pt" ? "Outro" : language === "es" ? "Otro" : "Other" },
  ];

  const benefits = [
    { icon: Users, text: language === "pt" ? "Onboarding dedicado" : language === "es" ? "Onboarding dedicado" : "Dedicated onboarding" },
    { icon: Headphones, text: language === "pt" ? "Suporte prioritário" : language === "es" ? "Soporte prioritario" : "Priority support" },
    { icon: Shield, text: language === "pt" ? "SLA garantido" : language === "es" ? "SLA garantizado" : "Guaranteed SLA" },
    { icon: Globe, text: language === "pt" ? "Deploy personalizado" : language === "es" ? "Implementación personalizada" : "Custom deployment" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // The API saves it (Admin > Contatos) and e-mails the Comercial list set there.
      const res = await fetch(getApiUrl("/api/v1/enterprise/contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, website: honeypot, language, source: "website" }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        toast({ title: "Error", description: language === "pt" ? "Erro ao enviar. Tente novamente." : language === "es" ? "Error al enviar. Inténtelo de nuevo." : "Error sending. Please try again.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: language === "pt" ? "Erro de conexão." : language === "es" ? "Error de conexión." : "Connection error.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all text-sm";

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "var(--fs-sage)", color: "#fff" }}>
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-display mb-3">{t.successTitle}</h1>
          <p className="text-muted-foreground mb-8">{t.successMsg}</p>
          <Link href="/"><Button variant="outline" className="rounded-full gap-2"><ArrowLeft className="w-4 h-4" />{language === "pt" ? "Voltar ao início" : language === "es" ? "Volver al inicio" : "Back to home"}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      <section className="section-padding">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
            {/* Left side - info */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                <h1 className="text-3xl md:text-4xl font-display mb-4">{t.title}</h1>
                <p className="text-muted-foreground leading-relaxed mb-8">{t.subtitle}</p>

                <div className="space-y-4 mb-8">
                  {benefits.map((b, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "hsla(95, 40%, 38%, 0.08)", color: "var(--fs-forest)" }}>
                        <b.icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{b.text}</span>
                    </div>
                  ))}
                </div>

                <div className="separator-botanical" />

                {/* Admin > Contatos in the app: blank phone or WhatsApp is not shown. */}
                <div className="space-y-3 text-sm text-muted-foreground" data-testid="contact-info">
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-foreground transition-colors"><Mail className="w-4 h-4" />{contact.email}</a>
                  {contact.phone && (
                    <a href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-2 hover:text-foreground transition-colors"><Phone className="w-4 h-4" />{contact.phone}</a>
                  )}
                  {contact.whatsAppDigits && (
                    <a href={`https://wa.me/${contact.whatsAppDigits}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-foreground transition-colors"><MessageCircle className="w-4 h-4" />{contact.whatsApp}</a>
                  )}
                  {contact.address && (
                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{language === "en" ? contact.address.replace(/Brasil$/, "Brazil") : contact.address}</div>
                  )}
                </div>
              </AnimatedSection>
            </div>

            {/* Right side - form */}
            <div className="lg:col-span-3">
              <AnimatedSection delay={0.2}>
                <form onSubmit={handleSubmit} className="card-organic p-6 sm:p-8 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.name}</label>
                      <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.email}</label>
                      <input type="email" className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.company}</label>
                      <input className={inputClass} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.phone}</label>
                      <input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.employees}</label>
                      <select className={inputClass} value={form.employees} onChange={(e) => setForm({ ...form, employees: e.target.value })}>
                        <option value="">--</option>
                        <option value="1-10">1-10</option>
                        <option value="11-50">11-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-1000">201-1000</option>
                        <option value="1000+">1000+</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.interest}</label>
                      <select className={inputClass} value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}>
                        {interests.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">{t.message}</label>
                    <textarea className={`${inputClass} min-h-[120px] resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  </div>
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                    style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }} />
                  <button type="submit" disabled={loading} className="btn-cta flex items-center gap-2 w-full justify-center disabled:opacity-50">
                    <Send className="w-4 h-4" />{loading ? t.sending : t.submit}
                  </button>
                </form>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-8 border-t text-center text-sm text-muted-foreground">
        <div className="container mx-auto px-4">© {new Date().getFullYear()} FillSign.</div>
      </footer>
    </div>
  );
}
